import { Check, CheckCircle } from "@mui/icons-material"
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material"
import { t } from "i18next"
import moment from "moment"
import { FC, PropsWithChildren, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { ActionReq } from "shared/actionReq"
import {
  ActionReqExecutionModeEnum,
  ActionReqThingTypeMrn,
  ActionReqTypeEnum,
} from "shared/actionReqData"
import { LockerDoorReservationSession } from "shared/lockerDoorReservationSession"
import { Reservation } from "shared/reservation"
import { REMINDER_MAX_COUNT } from "shared/reminderSchedule"
import { SstDoor } from "shared/sst"
import { useCreateThingsMutation } from "src/features/things/thingsApi"
import { useNextAutomaticReminderAt } from "src/hooks/useNextAutomaticReminderAt"
import { useReservation } from "src/hooks/useReservation"
import { v4 as uuid } from "uuid"
import { parseSst2Utc } from "../sst2DateUtils"
import { OrderReminderNotifPreview } from "./OrderReminderNotifPreview"
import { OsExtendExpirationDialog } from "./OsExtendExpirationDialog"
import { Sl2Btn } from "./Sl2Btn"

const SST2_DISPLAY_FORMAT = "YYYY-MM-DD HH:mm"

/** Format SST V2 naive-UTC (or ISO-Z) timestamps for the reservation stepper. */
function formatSst2Timestamp(value: string | null | undefined): string {
  const d = parseSst2Utc(value)
  return d ? moment(d).format(SST2_DISPLAY_FORMAT) : ""
}

type ReservationStepProps = PropsWithChildren<{
  title: string
  status?: string
  id: number
  isLast?: boolean
}>
const ReservationStep: FC<ReservationStepProps> = ({
  title,
  status,
  id,
  children,
  isLast,
}) => {
  return (
    <Stack key={id} direction="column" spacing={0} alignItems="center">
      <Paper
        variant="outlined"
        sx={{
          p: 1,
          width: "100%",
          borderColor: status === "pending" ? "secondary.main" : "grey.300",
          borderWidth: status === "pending" ? "2px" : "1px",
        }}
      >
        <Stack direction="column" spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                bgcolor: status === "done" ? "#43A047" : "grey.400",
                borderRadius: "50%",
                display: "flex",
                color: "white",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Check
                sx={{
                  fontSize: 18,
                }}
              />
            </Box>
            <Typography variant="body1" color="primary.main">
              {t(title)}
            </Typography>
            {/* {status && (
          <Box>
            <Chip label={status} />
          </Box>
        )} */}
          </Stack>
          {!isLast && (
            <Box
              sx={{
                pl: "13px",
              }}
            >
              <Box>
                {children && (
                  <Paper
                    sx={{
                      p: 1,
                      borderColor: "grey.100",
                      bgcolor: "grey.100",
                    }}
                    variant="outlined"
                  >
                    {children}
                  </Paper>
                )}
              </Box>
            </Box>
          )}
        </Stack>
      </Paper>
      {/* backgronud banner  */}
      <Box
        sx={{
          bgcolor: status === "done" ? "secondary.main" : "grey.100",
          width: "100px",
          height: isLast ? 50 : 10,
          borderRadius: isLast ? "0 0 50px 50px" : "0",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {isLast && (
          <CheckCircle
            sx={{ color: status === "done" ? "whitesmoke" : "grey.400" }}
          />
        )}
      </Box>
    </Stack>
  )
}
export type OsLockerReservationStepperProps = {
  door: SstDoor
  reservation?: Reservation
  session?: LockerDoorReservationSession
}

export const OsLockerReservationStepper: FC<
  OsLockerReservationStepperProps
> = ({ door, reservation, session }) => {
  const { t } = useTranslation()

  const { isCompleted, hasExpired, hasDeposited, hasPickedUp } = useReservation(
    {
      reservation,
      session,
      door,
      hasDoorLink: !!door.links?.find((l: any) => l?.rel === "reservation") || !!reservation?.sstReservation,
    },
  )
  const [createThings] = useCreateThingsMutation()

  // LOCKER-324 — undefined unless the tenant has automatic reminders on AND
  // this reservation still has one coming.
  const nextAutomaticReminderAt = useNextAutomaticReminderAt(reservation)

  // LOCKER-407: the reservation the preview was opened FOR, not a bare "is it
  // open" boolean. The drawer mounts this stepper once and swaps its props as
  // the operator changes door (`OsLockerDoorDrawer.tsx`, no key), so a boolean
  // survived the switch and the open preview re-bound to the new door — and
  // the confirm then sent to that recipient. Holding the id makes both the
  // visibility and the send read the same value, rather than one render apart.
  // Defect, prod evidence and the rejected alternatives: LOCKER-407. Behaviour
  // under a door switch: `OsLockerReservationStepper.reminderPreviewRebind.test.tsx`.
  const [reminderPreviewFor, setReminderPreviewFor] = useState<
    string | undefined
  >(undefined)

  const [isSendingReminder, setIsSendingReminder] = useState(false)

  const [snackMessage, setSnackMessage] = useState({
    message: "",
    severity: "success" as "success" | "error" | "warning" | "info" | undefined,
  })

  const onSendReminderConfirmed = useCallback(async () => {
    const previewedReservationId = reminderPreviewFor
    setReminderPreviewFor(undefined)
    if (isSendingReminder) {
      console.error("Already sending reminder")
      return
    }
    // LOCKER-407. The invariant that does the work is the line further down:
    // the ActionReq is built from `previewedReservationId`, so the send targets
    // the previewed reservation and nothing else. This guard adds no second
    // protection on the door-switch path — no caller can reach it with the two
    // ids disagreeing, because a render where they disagree renders no preview
    // to confirm from. It stays as the explicit statement of the invariant, and
    // it subsumes the `!reservation?.id` null check it replaced.
    if (!previewedReservationId || previewedReservationId !== reservation?.id) {
      console.error(
        "Reminder preview no longer matches the current reservation; refusing to send",
      )
      return
    }

    setIsSendingReminder(true)
    try {
      const actionReq: Partial<ActionReq> = {
        thingTypeMrn: ActionReqThingTypeMrn,
        executionMode: ActionReqExecutionModeEnum.sync,
        tenantId: reservation.tenantId,
        idempotencyKey: uuid(),
        actionType: ActionReqTypeEnum.SendOrderPickupReminder,
        inputData: {
          tenantId: reservation.tenantId,
          reservationId: previewedReservationId,
        },
      }
      await createThings({
        body: { things: [actionReq] },
        params: { tenantId: reservation.tenantId },
      }).unwrap()
    } catch (e: any) {
      console.error(e)
      setSnackMessage({
        message: t("sendReminderError"),
        severity: "error",
      })
    } finally {
      setIsSendingReminder(false)
    }
  }, [
    createThings,
    isSendingReminder,
    reminderPreviewFor,
    reservation?.id,
    reservation?.tenantId,
    t,
  ])

  // Non-undefined only while the open preview still belongs to the reservation
  // in front of the operator. Switching doors takes it to `undefined`, which
  // unmounts the preview instead of re-binding it.
  const activeReminderPreviewId =
    reminderPreviewFor && reminderPreviewFor === reservation?.id
      ? reminderPreviewFor
      : undefined

  const sstReservation = reservation?.sstReservation
  // Reminders are pointless once the deadline has passed — the message tells the
  // recipient to collect before a date that is already gone. Extending is the
  // action that applies at that point, which is why the two no longer share a
  // visibility rule.
  const showReminderButton =
    !hasExpired && (reservation?.nbReminderSent || 0) < REMINDER_MAX_COUNT

  // sanity check
  if (!door) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box data-ui="UI-SL2W-054"
      sx={{
        width: "100%",
      }}
    >
      <Stack direction="column" spacing={0} px={2}>
        <ReservationStep
          title={"reservationCreation"}
          id={1}
          status={sstReservation?._created ? "done" : "pending"}
        >
          {sstReservation?._created && (
            <Stack direction="column" spacing={1}>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="body2" color="primary.main">
                  {t("reservationCreatedAt")}
                </Typography>
                <Typography variant="body2" color="primary.main">
                  {"🕒  " + formatSst2Timestamp(sstReservation?._created)}
                </Typography>
              </Stack>
              {reservation?.employeeCode && (
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography variant="body2" color="primary.main">
                    {t("employeeCode")}
                  </Typography>
                  <Chip label={reservation?.employeeCode} variant="filled" />
                </Stack>
              )}
            </Stack>
          )}
        </ReservationStep>
        <ReservationStep
          title={"parcelDeposit"}
          id={3}
          status={hasDeposited ? "done" : "pending"}
        >
          {sstReservation?.delivery_time && (
            <Stack direction="column" spacing={2}>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="body1" color="primary.main">
                  {t("parcelDeposited")}
                </Typography>
                <Typography variant="body1" color="primary.main">
                  {"🕒  " +
                    formatSst2Timestamp(sstReservation?.delivery_time)}
                </Typography>
              </Stack>
            </Stack>
          )}
        </ReservationStep>
        <ReservationStep
          title={"parcelPickUp"}
          id={4}
          status={
            hasPickedUp
              ? "done"
              : !hasExpired && hasDeposited
                ? "pending"
                : undefined
          }
        >
          {hasPickedUp && sstReservation?.reception_time ? (
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="body1" color="primary.main">
                {t("parcelPickedUp")}
              </Typography>
              <Typography variant="body1" color="primary.main">
                {"🕒  " +
                  formatSst2Timestamp(sstReservation?.reception_time)}
              </Typography>
            </Stack>
          ) : !isCompleted && hasDeposited ? (
            // LOCKER-320: `!hasExpired` used to gate this whole branch, so the
            // step rendered nothing at all once the deadline passed — taking the
            // extend control with it. The branch is now lifecycle-only
            // (deposited, not picked up) and each control states its own rule.
            <Stack
              direction="column"
              spacing={2}
              alignItems="center"
              // justifyContent="space-between"
            >
              {showReminderButton && (
                <Sl2Btn
                  variant="outlined"
                  onClick={() => setReminderPreviewFor(reservation?.id)}
                  disabled={isSendingReminder}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    {isSendingReminder && <CircularProgress size={16} />}{" "}
                    {t("sendReminder")}
                  </Stack>
                </Sl2Btn>
              )}
              <OsExtendExpirationDialog
                // `key` is load-bearing, not a list-rendering formality.
                //
                // The door drawer mounts this stepper ONCE and swaps its props
                // as the operator clicks from door to door
                // (`OsLockerDoorDrawer.tsx`, no key), so without this the
                // dialog keeps every piece of its own state across that switch
                // — including the "The new expiration date is …" confirmation.
                // An operator who extended door 32 then opened door 34 was
                // shown door 32's success line under door 34's reservation,
                // which reads as a claim about the reservation in front of
                // them. Keying on the reservation remounts the component, which
                // resets the confirmation, the open/closed state, the selected
                // duration and the in-flight flag together — a `useEffect` that
                // cleared them one by one would silently miss the next piece of
                // state someone adds.
                key={reservation?.id}
                reservation={reservation}
                hasExpired={hasExpired}
                showTopDivider={showReminderButton}
                onError={(message) =>
                  setSnackMessage({ message, severity: "error" })
                }
              />

              {!hasExpired && reservation?.reminderLastSent && (
                <Typography variant="caption" color="primary.main">
                  {`${
                    t("reminderLastSent") +
                    " " +
                    moment(reservation?.reminderLastSent).format(
                      "YYYY-MM-DD HH:mm:ss",
                    )
                  }`}
                </Typography>
              )}
            </Stack>
          ) : null}
        </ReservationStep>
        {/* reveraiontFinished */}
        <ReservationStep
          title={"reservationFinished"}
          id={5}
          status={hasPickedUp ? "done" : undefined}
          isLast
        >
          {(sstReservation?.status === "CLOSE" || sstReservation?.status === "CLOSED") && (
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="body1" color="primary.main">
                {t("reservationFinished")}
              </Typography>
              <Typography variant="body1" color="primary.main">
                {"🕒  " +
                  formatSst2Timestamp(sstReservation?.reception_time)}
              </Typography>
            </Stack>
          )}
        </ReservationStep>
        {activeReminderPreviewId && (
          <OrderReminderNotifPreview
            onCancel={() => setReminderPreviewFor(undefined)}
            onOk={onSendReminderConfirmed}
            reservationId={activeReminderPreviewId}
            firstName={reservation?.firstName}
            lastName={reservation?.lastName}
            email={reservation?.email}
            phone={reservation?.phone}
            nextAutomaticReminderAt={nextAutomaticReminderAt}
          />
        )}
      </Stack>
      <Snackbar
        open={!!snackMessage?.message}
        autoHideDuration={6000}
        onClose={() => setSnackMessage({ message: "", severity: undefined })}
      >
        <Alert severity={snackMessage.severity}>{snackMessage.message}</Alert>
      </Snackbar>
    </Box>
  )
}

export default OsLockerReservationStepper
